import {
  resetShiftArrowSelectState,
  shiftArrowSelectReducer,
  toggleGroupSelection,
  toggleRowSelection,
} from './utils/multiselect';
import rowWrapper from './utils/rowWrapper';
import React, {
  Context,
  Dispatch,
  forwardRef,
  KeyboardEvent,
  MutableRefObject,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { Set } from 'immutable';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { isDownHandler, isUpHandler, metaSymbol, metaTrigger } from '@support/keyboardHandler';
import { SelectAllIcon } from '@components/Icons';
import KeyboardShortcut from '@components/KeyboardShortcut';
import DropdownMenu from '@components/Menus/DropdownMenu';
import styles from './VirtualizedList.module.scss';
import { ID, UserAction } from '@app/types';
import BaseObject from '@models/BaseObject';
import { useDndMonitor } from '@dnd-kit/core';
import analytics from '@support/analytics';
import { useHotkeyHandler } from '@hooks/use-hotkey-handler';
import { addTimeSeparators } from './utils/time-separators';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsModalOpen } from '@actions/ui';

export type HighlightEvtType = 'keyboard' | 'mouse';

export interface ListProps<T> {
  items: Array<T>;
  shouldResetHighlight: boolean;
  canLoadMore: boolean;
  ListContext: Context<ListState>;
  renderItem: (
    item: T,
    isHighlighted: boolean,
    multipleResultsSelected: boolean,
    index: number,
  ) => ReactElement<any, any>;
  onItemClick?: (item: T, e: SyntheticEvent) => void;
  onItemHighlight: (item: T) => void;

  multiselect?: boolean;
  draggableItems?: boolean;
  headerRef?: MutableRefObject<HTMLElement>;
  headerContent?: ReactElement<any, any>;
  dropdownMenuOptions?: Array<UserAction>;
  selectedItems?: Set<ID>;
  topPadding?: number;
  headerCutoff?: number;
  estimatedCount?: number;
  listRef?: MutableRefObject<VariableSizeList<any>>;
  listRowClassName?: string;
  itemBasedListRowClassName?: (id: ID) => string;
  emptyListView?: ReactNode;
  beforeHeaderActions?: ReactNode;
  disabled?: boolean;
  setSelectedItems?: Dispatch<Set<ID>>;
  loadMore?: () => void;
  onHotKey?: (item: T, e: KeyboardEvent) => void;
  // determineItemSize?: (idx: number) => number;
  determineItemSize?: (item: T) => number;
  defaultHighlightedIdx?: number;
  forceResetHighlightedIdx?: string;
  contextMenuTarget?: 'Contact' | 'ArchivedContact';
  contextMenuDisabled?: boolean;
  timeBucketKey?: string;
  active?: boolean;
  indicateRowLoadingState?: boolean;
  hasListHeader?: boolean;
}

export interface ListState {
  highlightedIdx: number;
  highlightEvtType: HighlightEvtType;
  selectedItems: Set<ID>;
  lastToggledCheckboxIdx: number;
  contextMenuTarget: 'Contact' | 'ArchivedContact';
  contextMenuDisabled: boolean;
  listActive: boolean;
  indicateRowLoadingState: boolean;
}

const defaultListState: ListState = {
  highlightedIdx: 0,
  highlightEvtType: 'mouse',
  selectedItems: Set<ID>(),
  lastToggledCheckboxIdx: -1,
  contextMenuTarget: 'Contact',
  contextMenuDisabled: false,
  listActive: true,
  indicateRowLoadingState: true,
};

// This is used to efficiently share state with all children of a List instance. While
// the parent of <List/> controls the selectedItems (so that it can access these for any actions that
// fall outside the domain of <List/>), passing selection state down to each row of a List via props
// forces a rerender of each row on every change to selectedItems (or to highlightedIdx, which is a
// much more frequent event). In order to use Context while still being able to reuse this component,
// there must be a Context created and passed into <List/> per instance.
export const createListContext = () => React.createContext<ListState>({ ...defaultListState });

const hasNumericID = (item: any) => /\d/g.test(item?.id);

function VirtualizedList<T extends BaseObject>(
  {
    items,
    renderItem,
    shouldResetHighlight,
    onItemClick,
    onHotKey = null,
    canLoadMore,
    loadMore = () => {},
    selectedItems = Set<ID>(),
    setSelectedItems = () => {},
    multiselect,
    headerRef,
    headerContent = null,
    dropdownMenuOptions,
    ListContext,
    topPadding = 48,
    estimatedCount,
    onItemHighlight = null,
    headerCutoff = 0,
    determineItemSize = null,
    listRowClassName = null,
    itemBasedListRowClassName,
    listRef = null,
    emptyListView = null,
    beforeHeaderActions = null,
    disabled = false,
    draggableItems = false,
    defaultHighlightedIdx = 0,
    forceResetHighlightedIdx,
    contextMenuTarget = 'Contact',
    contextMenuDisabled = false,
    timeBucketKey,
    active = true,
    indicateRowLoadingState = true,
    hasListHeader = false,
  }: ListProps<T>,
  passedRef: MutableRefObject<HTMLDivElement>,
) {
  const isMultiselect = !!multiselect;
  const listContainer = useRef(null);
  const _listRef = useRef<VariableSizeList<any>>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [[highlightedIdx, highlightEvtType, highlightedWhileShiftKey], setHighlightedIdx] =
    useState<[number, HighlightEvtType, boolean]>([
      defaultHighlightedIdx || defaultListState.highlightedIdx,
      'mouse',
      false,
    ]);
  const [lastToggledCheckboxIdx, setLastToggledCheckboxIdx] = useState(
    defaultListState.lastToggledCheckboxIdx,
  );
  const [shiftArrowSelectState, dispatchShiftArrowSelect] = useReducer(
    shiftArrowSelectReducer,
    resetShiftArrowSelectState(-1),
  );
  const [loadingMoreToSelectAll, setLoadingMoreToSelectAll] = useState(false);
  const [lastItemCount, setLastItemCount] = useState(items.length);
  const [currentlyDragging, setCurrentlyDragging] = useState(false);
  const navigate = useNavigate();
  const modalIsOpen = useSelector(selectIsModalOpen);

  // We add rows in different contexts, so may need to adjust the default highlighted idx accordingly
  const resetHighlightedIndexRelativeToAdjustedItems = (index: number) => {
    if (items.length === 0) {
      return;
    }
    const highlightedItem = items[index || 0];
    const indexInAdjustedItems = itemData.items.findIndex(({ id }) => id == highlightedItem?.id);
    setHighlightedIdx([indexInAdjustedItems, 'mouse', false]);
  };

  useDndMonitor({
    onDragStart: (e) => {
      if (e.active?.data?.current?.type == 'contactIDs') {
        setCurrentlyDragging(true);
      }
    },
    onDragEnd: (e) => {
      if (e.active?.data?.current?.type == 'contactIDs') {
        setCurrentlyDragging(false);
      }
    },
    onDragCancel: () => setCurrentlyDragging(false),
  });

  useEffect(() => {
    if (forceResetHighlightedIdx && defaultHighlightedIdx) {
      resetHighlightedIndexRelativeToAdjustedItems(defaultHighlightedIdx);
      // setHighlightedIdx([defaultHighlightedIdx, 'keyboard', false]);
    }
  }, [forceResetHighlightedIdx]);

  const [lastItemsPassedIn, setLastItemsPassedIn] = useState(items);

  const itemData = useMemo(() => {
    // @ts-ignore
    let itemsToRender = [{ id: 'paddingRow' }].concat(items);
    let timeSeparatorIndices = [];

    if (timeBucketKey) {
      const withTimeSeparators = addTimeSeparators(itemsToRender, timeBucketKey);
      itemsToRender = withTimeSeparators.items;
      timeSeparatorIndices = withTimeSeparators.separatorIndices;
    }

    return {
      items: itemsToRender,
      renderItem,
      setHighlightedIdx,
      setSelectedItems,
      setLastToggledCheckboxIdx,
      onItemClick,
      ListContext,
      isMultiselect,
      listRowClassName,
      itemBasedListRowClassName,
      disabled,
      canDrag: !!draggableItems,
      timeSeparatorIndices,
      scrollToIndex: (idx: number) => {
        _listRef.current.scrollToItem(idx, 'center');
      },
    };
    // This is insanity, but the data seems to sometimes get memoized *before* navigate is properly wired
    // up. If the handlers passed in the component props include navigate, those handlers may be broken
    // in these instances. This is a hack to force the data to be re-memoized when navigate is properly wired up.
  }, [items, disabled, navigate]);

  const selectableRows = useMemo(() => items.filter(hasNumericID), [items]);
  const highlightedItem = highlightedIdx > 0 && itemData.items[highlightedIdx];
  const allRowsSelected = selectedItems.size == selectableRows.length;
  const highlightedItemInSelection = highlightedItem && selectedItems.has(highlightedItem.id);

  const toggleHandlerCtx = {
    selectedItems,
    lastToggledCheckboxIdx,
    setSelectedItems,
    setLastToggledCheckboxIdx,
  };

  const selectHighlightedItem = () => {
    const id = itemData.items[highlightedIdx].id;
    setSelectedItems(selectedItems.add(id));
    setLastToggledCheckboxIdx(highlightedIdx);
  };

  const updateHighlightPos = (
    nextIdx: number,
    shiftKeyPressed: boolean,
    direction: 'up' | 'down',
  ) => {
    let nextHighlightedIdx = nextIdx;

    if (itemData.timeSeparatorIndices.includes(nextHighlightedIdx)) {
      nextHighlightedIdx = direction == 'up' ? nextHighlightedIdx - 1 : nextHighlightedIdx + 1;
    }

    setHighlightedIdx([nextHighlightedIdx, 'keyboard', false]);

    if (shiftKeyPressed && isMultiselect) {
      dispatchShiftArrowSelect(['update', nextHighlightedIdx]);
    }

    if (
      shiftKeyPressed &&
      isMultiselect &&
      !shiftArrowSelectState.shouldUpdateSelection &&
      highlightedIdx > 0
    ) {
      // This state means that we have reset the shiftArrowSelectState with either
      // a new shift key press or by mousing into a row with the shift key pressed.
      // We have selected an anchor for the shift-arrow selection mechanism, and this
      // highlight position movement is going to select the highlighted item in the
      // next frame. We select the anchor item (the row that is currently highlighted
      // in *this* frame) so that it appears checked in the very next frame. Otherwise
      // we can get an odd frame with no selection state before the anchor and the focus
      // of the shiftArrowSelectState can be selected.
      selectHighlightedItem();
    }
  };

  const moveHighlightDown = (shiftKeyPressed: boolean) => {
    if (highlightedIdx < itemData.items.length - 1) {
      updateHighlightPos(highlightedIdx + 1, shiftKeyPressed, 'down');
    }
  };

  const moveHighlightUp = (shiftKeyPressed: boolean) => {
    const topmostIdx = timeBucketKey || hasListHeader ? 2 : 1;
    if (highlightedIdx > topmostIdx) {
      updateHighlightPos(highlightedIdx - 1, shiftKeyPressed, 'up');
    }
  };

  const toggleAllRowsSelected = useCallback(
    (e?: SyntheticEvent) => {
      if (allRowsSelected) {
        setSelectedItems(Set());

        if (loadingMoreToSelectAll) {
          setLoadingMoreToSelectAll(false);
        }
      } else {
        if (canLoadMore) {
          loadMore();
          setLoadingMoreToSelectAll(true);
        }

        setSelectedItems(Set(selectableRows.map(({ id }) => id)));
        analytics.track('Search Results All Selected', { estimatedCount });
      }

      e && (e.target as HTMLElement).blur();
    },
    [selectableRows, allRowsSelected, canLoadMore, estimatedCount, loadingMoreToSelectAll],
  );

  const shouldSkipHotkeyHandling = (e): boolean =>
    e.defaultPrevented || window.__activeConfirm || disabled || !active;

  const itemSize = (idx) => {
    if (idx === 0) {
      return topPadding;
    }

    if (itemData.timeSeparatorIndices.includes(idx)) {
      return 36;
    }

    if (determineItemSize) {
      // @ts-ignore
      return determineItemSize(itemData.items[idx]);
    }

    return 44;
  };

  useEffect(() => {
    listRef?.current?.resetAfterIndex(0);
  }, [itemData.items]);

  useHotkeyHandler(
    {
      handler: (e: KeyboardEvent) => {
        if (shouldSkipHotkeyHandling(e)) {
          return;
        }

        if (isMultiselect && e.key === 'Shift' && highlightedIdx) {
          dispatchShiftArrowSelect(['reset', highlightedIdx]);
        }

        if (isUpHandler(e) && !modalIsOpen) {
          e.preventDefault();
          return moveHighlightUp(e.shiftKey);
        } else if (isDownHandler(e) && !modalIsOpen) {
          e.preventDefault();
          return moveHighlightDown(e.shiftKey);
        } else if (e.keyCode === 13 && highlightedItem) {
          onItemClick?.(highlightedItem as T, e);
          return e.preventDefault();
        } else if (isMultiselect && (e.keyCode === 32 || e.keyCode == 88) && highlightedItem) {
          e.preventDefault();

          if (e.shiftKey) {
            return toggleGroupSelection(
              toggleHandlerCtx,
              itemData.items,
              highlightedItem,
              highlightedIdx,
            );
          } else {
            return toggleRowSelection(toggleHandlerCtx, highlightedItem, highlightedIdx);
          }
        } else if (isMultiselect && metaTrigger(e) && e.key === 'a') {
          e.preventDefault();
          toggleAllRowsSelected();
          return;
        }

        onHotKey && onHotKey(itemData.items[highlightedIdx] as T, e);
      },
    },
    [
      active,
      itemData.items,
      highlightedIdx,
      selectedItems,
      lastToggledCheckboxIdx,
      shiftArrowSelectState.shouldUpdateSelection,
      modalIsOpen,
    ],
  );

  useHotkeyHandler(
    {
      priority: 1,
      handler: (e) => {
        if (shouldSkipHotkeyHandling(e)) {
          return;
        }

        if (isMultiselect && e.keyCode == 27 && selectedItems.size > 0) {
          setSelectedItems(Set());
          e.preventDefault();
        }
      },
    },
    [items, selectedItems],
  );

  useEffect(() => {
    if (!onItemHighlight || itemData.items.length <= 1) {
      return;
    }

    // @ts-ignore
    onItemHighlight(itemData.items[highlightedIdx]);
  }, [highlightedIdx, itemData.items.length]);

  useEffect(() => {
    if (shouldResetHighlight) {
      setHighlightedIdx([0, 'keyboard', false]);
      setSelectedItems(Set());
      setLastToggledCheckboxIdx(-1);
    }
  }, [items]);

  useEffect(() => {
    const { anchorIdx, focusIdx, minIdx, maxIdx, shouldUpdateSelection } = shiftArrowSelectState;

    if (isMultiselect && shouldUpdateSelection) {
      const fullSelectionEventSet = Set(
        itemData.items.slice(minIdx, maxIdx + 1).map(({ id }) => id),
      );
      const selectionStartIdx = Math.min(anchorIdx, focusIdx);
      const selectionEndIdx = Math.max(anchorIdx, focusIdx);
      const selectedIDSet = Set(
        itemData.items.slice(selectionStartIdx, selectionEndIdx + 1).map(({ id }) => id),
      );
      const deselectedIDSet = fullSelectionEventSet.subtract(selectedIDSet);
      const newSelectedItems = selectedItems.subtract(deselectedIDSet).concat(selectedIDSet);
      // @ts-ignore: concat method will return a Set<string>
      setSelectedItems(newSelectedItems);
    }
  }, [shiftArrowSelectState]);

  // If we've removed some items from the list, reset focus and selection set
  useEffect(() => {
    if (
      isMultiselect &&
      items.length < lastItemCount &&
      (selectedItems.size > 0 || lastToggledCheckboxIdx != -1)
    ) {
      setSelectedItems(Set());
      setLastToggledCheckboxIdx(-1);
    }

    if (items.length < lastItemCount && highlightedIdx >= itemData.items.length) {
      resetHighlightedIndexRelativeToAdjustedItems(Math.max(items.length - 1, 0));
    }

    setLastItemCount(items.length);
  }, [items]);

  useEffect(() => {
    if (listContainer.current) {
      const height = listContainer.current.offsetHeight || window.innerHeight;
      setContainerHeight(height);
    }
  }, []);

  useEffect(() => {
    if (loadingMoreToSelectAll && !canLoadMore) {
      setSelectedItems(Set(items.filter(hasNumericID).map(({ id }) => id)));
      setLoadingMoreToSelectAll(false);
    }
  }, [canLoadMore, loadingMoreToSelectAll]);

  useEffect(() => {
    if (loadingMoreToSelectAll && canLoadMore) {
      setSelectedItems(Set(items.filter(hasNumericID).map(({ id }) => id)));
      loadMore();
    }
  }, [items.length, canLoadMore, loadingMoreToSelectAll]);

  useEffect(() => {
    // If we mouse into a row with shiftKey down, we should reset the shift arrow selection
    // state. Resetting when the shift key is initially pressed (if there is a valid
    // highlightedIdx) is handled separately in the hotkey handler. Keyboard events that
    // update highlightedIdx also are handled separately -- these result in an 'update' being
    // dispatched to the shiftArrowSelectionState, rather than a 'reset'.
    if (isMultiselect && highlightedWhileShiftKey) {
      dispatchShiftArrowSelect(['reset', highlightedIdx]);
    }

    if (highlightedIdx < 0 || highlightEvtType != 'keyboard') {
      return;
    }
  }, [highlightedIdx]);

  useEffect(() => {
    const handleResize = () => {
      if (listContainer.current) {
        setContainerHeight(listContainer.current.offsetHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (lastItemsPassedIn.length === 0 && items.length > 0) {
      resetHighlightedIndexRelativeToAdjustedItems(defaultHighlightedIdx || 0);
    }
    setLastItemsPassedIn(items);
  }, [items]);

  useEffect(() => {
    if (items.length > 0) {
      resetHighlightedIndexRelativeToAdjustedItems(defaultHighlightedIdx || 0);
    }
  }, []);

  let header = null;

  if (isMultiselect) {
    header = (
      <>
        <div className={styles.multiselectHeader}>
          {beforeHeaderActions}
          {items.length > headerCutoff && (
            <>
              {headerContent}
              {items.length > 1 && (
                <KeyboardShortcut keys={[metaSymbol, 'a']} placement="bottom" label="Select all">
                  <SelectAllIcon
                    className={cn(styles.selectAllToggle, allRowsSelected && styles.allSelected)}
                    onClick={toggleAllRowsSelected}
                  />
                </KeyboardShortcut>
              )}
            </>
          )}
          {dropdownMenuOptions && (
            <DropdownMenu options={dropdownMenuOptions} menuClassName={styles.dropdownMenu} />
          )}
        </div>
      </>
    );
  }

  return (
    <ListContext.Provider
      value={{
        highlightedIdx,
        highlightEvtType,
        selectedItems,
        lastToggledCheckboxIdx,
        contextMenuTarget,
        contextMenuDisabled,
        listActive: active,
        indicateRowLoadingState,
      }}
    >
      {header && headerRef && headerRef.current && ReactDOM.createPortal(header, headerRef.current)}
      <div
        className={cn(
          styles.container,
          currentlyDragging && styles.dragInProgress,
          highlightedItemInSelection && styles.highlightedItemInSelection,
        )}
        ref={listContainer}
        // inlined style fixes issue with delayed layout with SSR
        style={{
          height: '100vh',
          transform: `translateY(-${topPadding}px)`,
          top: topPadding,
        }}
      >
        {items.length == 0 && !canLoadMore && emptyListView ? (
          emptyListView
        ) : (
          <InfiniteLoader
            isItemLoaded={(idx) => !canLoadMore || idx < items.length}
            itemCount={canLoadMore ? items.length + 1 : items.length}
            loadMoreItems={canLoadMore ? loadMore : () => {}}
          >
            {({ onItemsRendered, ref }) => {
              return (
                <VariableSizeList
                  ref={(list) => {
                    ref(list);
                    _listRef.current = list;

                    if (listRef) {
                      listRef.current = list;
                    }
                  }}
                  onItemsRendered={onItemsRendered}
                  itemData={itemData}
                  height={containerHeight}
                  width="100%"
                  itemCount={
                    items.length > 0 ? items.length + 1 + itemData.timeSeparatorIndices.length : 0
                  }
                  itemSize={itemSize}
                  overscanCount={5}
                  outerRef={passedRef}
                >
                  {rowWrapper}
                </VariableSizeList>
              );
            }}
          </InfiniteLoader>
        )}
      </div>
    </ListContext.Provider>
  );
}

export const List = forwardRef(VirtualizedList);
